import { Input } from '@headlessui/react'
import clsx from 'clsx'
import { Eye, EyeOff } from 'lucide-react'
import { forwardRef, useState } from 'react'
import { ChangeHandler } from 'react-hook-form'

type PasswordInputProps = {
  name?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: ChangeHandler
  size?: 'sm' | 'md'
  className?: string
  label?: string
  placeholder?: string
  error?: string
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ name, onChange, onBlur, className, label, size = 'sm', placeholder, error }, ref) => {
    const [isShow, setIsShow] = useState(false)

    const inputClasses = clsx(
      'block relative bg-light-bg dark:bg-card-background border-0 ring w-full text-text-main dark:text-white',
      'focus:ring-2 placeholder-text-secondary',
      {
        'rounded-lg px-3 py-2': size === 'sm',
        'rounded-2xl px-4 py-3': size === 'md',
        'ring-error focus:outline-2 focus:outline-error': !!error,
        'ring-card-border dark:ring-card-border-dark focus:outline-card-border dark:focus:outline-card-border-dark':
          !error,
      },
      className
    )

    return (
      <div>
        <div className="relative flex flex-col gap-2">
          {label && <label className="text-text-main dark:text-text-secondary">{label}</label>}
          <Input
            ref={ref}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            type={isShow ? 'text' : 'password'}
            placeholder={placeholder}
            className={inputClasses}
          />
          <button
            type="button"
            onClick={() => setIsShow((prev) => !prev)}
            className="top-[50%] right-4 absolute -translate-y-[50%]"
          >
            {isShow ? (
              <EyeOff className="w-5 h-5 text-text-secondary" />
            ) : (
              <Eye className="w-5 h-5 text-text-secondary" />
            )}
          </button>
        </div>
        {error && (
          <span className="inline-block w-full text-error text-xs text-right">{error}</span>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
